import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { PROPOSAL_BUNDLE_CATEGORY, type ProposalBundle } from '../../types/proposalBundles';
import type { ChatMessage, FeedbackType } from '../../types/messages';
import AiAvatar from '../icons/AiAvatar';
import { AnimatedMessageRow } from '../AnimatedMessageRow';
import ProposalCards from './ProposalCards';
import { ForeignFlightProposalSection } from './ForeignFlightProposalSection';
import { formatTimestamp } from '../../utils/formatTimestamp';

const isForeignRoundTripLegBundle = (bundle: ProposalBundle): boolean =>
  (bundle.category === PROPOSAL_BUNDLE_CATEGORY.OUTWARD ||
    bundle.category === PROPOSAL_BUNDLE_CATEGORY.HOMEWARD) &&
  Boolean(bundle.hasForeignFlightRoundTrip);

const hasForeignRoundTripPair = (bundles: ProposalBundle[]): boolean => {
  const outward = bundles.some(
    bundle => bundle.category === PROPOSAL_BUNDLE_CATEGORY.OUTWARD && bundle.hasForeignFlightRoundTrip
  );
  const homeward = bundles.some(
    bundle => bundle.category === PROPOSAL_BUNDLE_CATEGORY.HOMEWARD && bundle.hasForeignFlightRoundTrip
  );
  return outward && homeward;
};

const isForeignRoundTripLegMessage = (bundles: ProposalBundle[]): boolean => {
  if (bundles.length === 0) {
    return false;
  }
  if (hasForeignRoundTripPair(bundles)) {
    return true;
  }
  if (bundles.some(bundle => bundle.category === PROPOSAL_BUNDLE_CATEGORY.HOTEL)) {
    return false;
  }
  return bundles.some(bundle => isForeignRoundTripLegBundle(bundle));
};

interface Props {
  message: ChatMessage;
  bundles: ProposalBundle[];
  tripId: number;
  includeTax: boolean;
  disabled?: boolean;
  onSelectItem?: (bundleId: number, itemId: number) => void;
  onCloseItem?: (bundleId: number) => void;
  onBundleFeedbackUpdate?: (bundleId: number, feedback: FeedbackType, commentSubmitted: boolean) => void;
  isSpLayout?: boolean;
}

const ProposalSection = (props: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  if (isForeignRoundTripLegMessage(props.bundles)) {
    return <ForeignFlightProposalSection {...props} />;
  }

  const {
    message,
    bundles,
    tripId,
    includeTax,
    disabled = false,
    onSelectItem,
    onCloseItem,
    onBundleFeedbackUpdate,
    isSpLayout = false
  } = props;
  return (
    <AnimatedMessageRow key={message.id} isNew={message.isNew} isSpLayout={isSpLayout}>
      <div style={styles.avatar}>
        <AiAvatar />
      </div>
      <div style={styles.messageContentAiProposal}>
        <ProposalCards
          bundles={bundles}
          includeTax={includeTax}
          introMessage={message.text}
          onSelect={onSelectItem}
          onClose={onCloseItem}
          onFeedbackUpdate={onBundleFeedbackUpdate}
          disabled={disabled}
          tripId={tripId}
          isSpLayout={isSpLayout}
        />
        <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
      </div>
    </AnimatedMessageRow>
  );
};

const getStyles = (_theme: AITTheme) => ({
  avatar: {
    flex: '0 0 48px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  messageContentAiProposal: {
    flex: 1,
    minWidth: 0,
    maxWidth: '100%'
  },
  timestamp: {
    fontSize: '11px',
    color: '#7d7d7d',
    marginTop: '4px'
  }
});

export default ProposalSection;
